import { useMutation } from '@tanstack/react-query'
import type { SubmitEvent } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import type { UserResponse } from '@/api/responses'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '@/components/ui'

import { register } from './api'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MIN_PASSWORD_LENGTH = 12

interface FieldErrors {
  displayName?: string
  email?: string
  password?: string
  confirmPassword?: string
}

export interface SignupPageProps {
  readonly onSuccess: (user: UserResponse) => void
  readonly onNavigateToLogin: () => void
}

export function SignupPage({ onSuccess, onNavigateToLogin }: SignupPageProps) {
  const { t } = useTranslation()
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const mutation = useMutation({
    mutationFn: register,
    onSuccess,
  })

  function validate(): FieldErrors {
    const errors: FieldErrors = {}

    if (displayName.trim().length < 2) {
      errors.displayName = t('auth.signup.errors.nameRequired')
    }

    if (!EMAIL_PATTERN.test(email)) {
      errors.email = t('auth.signup.errors.emailRequired')
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      errors.password = t('auth.signup.errors.passwordLength')
    } else if (password !== confirmPassword) {
      errors.confirmPassword = t('auth.signup.errors.passwordMismatch')
    }

    return errors
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>): void {
    event.preventDefault()
    const errors = validate()
    setFieldErrors(errors)

    if (Object.keys(errors).length > 0) {
      return
    }

    mutation.mutate({ displayName: displayName.trim(), email, password })
  }

  const apiMessages = describeApiError(mutation.error)
  const [firstMessage, ...restMessages] = apiMessages

  return (
    <main className="flex min-h-full items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>{t('auth.signup.title')}</CardTitle>
            <CardDescription>{t('auth.signup.subtitle')}</CardDescription>
          </CardHeader>
          <form
            onSubmit={(event) => {
              handleSubmit(event)
            }}
            noValidate
          >
            <CardContent className="flex flex-col gap-4">
              {firstMessage !== undefined ? (
                <Alert variant="danger" title={firstMessage}>
                  {restMessages.length > 0 ? (
                    <ul className="list-disc pl-4">
                      {restMessages.map((message) => (
                        <li key={message}>{message}</li>
                      ))}
                    </ul>
                  ) : null}
                </Alert>
              ) : null}
              <Input
                id="signup-name"
                type="text"
                label={t('auth.signup.nameLabel')}
                autoComplete="name"
                required
                value={displayName}
                onChange={(event) => {
                  setDisplayName(event.target.value)
                }}
                error={fieldErrors.displayName}
              />
              <Input
                id="signup-email"
                type="email"
                label={t('auth.signup.emailLabel')}
                autoComplete="email"
                required
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value)
                }}
                error={fieldErrors.email}
              />
              <Input
                id="signup-password"
                type="password"
                label={t('auth.signup.passwordLabel')}
                autoComplete="new-password"
                required
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value)
                }}
                error={fieldErrors.password}
                hint={
                  fieldErrors.password === undefined ? t('auth.signup.passwordHint') : undefined
                }
              />
              <Input
                id="signup-confirm-password"
                type="password"
                label={t('auth.signup.confirmPasswordLabel')}
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(event) => {
                  setConfirmPassword(event.target.value)
                }}
                error={fieldErrors.confirmPassword}
              />
            </CardContent>
            <CardFooter className="flex-col items-stretch gap-3">
              <Button type="submit" loading={mutation.isPending} className="w-full">
                {mutation.isPending ? t('auth.signup.submitting') : t('auth.signup.submit')}
              </Button>
              <p className="text-center text-sm text-foreground-muted">
                {t('auth.signup.loginPrompt')}{' '}
                <button
                  type="button"
                  onClick={onNavigateToLogin}
                  className="font-medium text-action-primary hover:underline"
                >
                  {t('auth.signup.loginLink')}
                </button>
              </p>
            </CardFooter>
          </form>
        </Card>
      </div>
    </main>
  )
}
